(() => {
  const GLOBAL_KEY = '__jdFooterAccordion';
  const SELECTOR = '[data-footer-accordion]';
  const DESKTOP_QUERY = '(min-width: 990px)';
  const BOTTOM_MENU_SELECTOR = '.footer__menu-bottom';
  const MOBILE_QUERY = '(max-width: 989px)';
  const ACCORDION_STYLE_ID = 'jd-footer-mobile-accordion-layout';

  const mediaQuery = window.matchMedia(DESKTOP_QUERY);
  const mobileQuery = window.matchMedia(MOBILE_QUERY);
  const mobileState = new WeakMap();
  const managed = new Set();

  const ensureAccordionStyles = () => {
    let style = document.getElementById(ACCORDION_STYLE_ID);

    if (!style) {
      style = document.createElement('style');
      style.id = ACCORDION_STYLE_ID;
      document.head.appendChild(style);
    }

    style.textContent = `
      @media screen and (max-width: 989px) {
        .footer .footer-block--accordion {
          display: block !important;
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          border-bottom: 0.1rem solid rgb(var(--color-border)) !important;
        }

        .footer .footer-block--logo + .footer-block--accordion {
          margin-top: 3.2rem !important;
          border-top: 0.1rem solid rgb(var(--color-border)) !important;
        }

        .footer .footer-block--accordion .footer-block__collapsible {
          display: block !important;
          width: 100% !important;
          margin: 0 !important;
        }

        .footer .footer-block--accordion .footer-block__summary {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          width: 100% !important;
          min-height: 7rem !important;
          margin: 0 !important;
          padding: 2rem 0 !important;
          gap: 2rem !important;
          cursor: pointer !important;
        }

        .footer .footer-block--accordion .footer-block__heading {
          flex: 1 1 auto !important;
          margin: 0 !important;
          padding: 0 !important;
          line-height: 1.2 !important;
          text-align: left !important;
        }

        .footer .footer-block--accordion .footer-block__summary .icon-caret {
          display: block !important;
          flex: 0 0 auto !important;
          width: 2rem !important;
          height: 2rem !important;
          margin: 0 1rem 0 0 !important;
        }

        .footer .footer-block--accordion .footer-block__details-content {
          width: 100% !important;
          margin: 0 !important;
          padding: 0 0 2rem !important;
          text-align: left !important;
        }

        .footer .footer-block--accordion .footer-block__details-content li:first-child .link {
          padding-top: 0.8rem !important;
        }

        .footer .footer-block--accordion .footer-block__details-content .list-menu__item--link {
          display: block !important;
          padding-block: 0.8rem !important;
          text-align: left !important;
        }

        .footer .footer-block--accordion + .footer-block--subscribe {
          margin-top: 4rem !important;
        }
      }
    `;
  };

  ensureAccordionStyles();

  const clearBottomMenuStyles = (menu) => {
    ['width', 'justify-content', 'text-align', 'margin-left', 'margin-right'].forEach((property) =>
      menu.style.removeProperty(property)
    );

    Array.from(menu.children).forEach((item) => {
      item.style.removeProperty('text-align');
    });
  };

  const applyBottomMenuAlignment = (root = document) => {
    const menus = [];

    if (root.matches?.(BOTTOM_MENU_SELECTOR)) menus.push(root);
    root.querySelectorAll?.(BOTTOM_MENU_SELECTOR).forEach((menu) => menus.push(menu));

    menus.forEach((menu) => {
      if (!mobileQuery.matches) {
        clearBottomMenuStyles(menu);
        return;
      }

      menu.style.setProperty('width', '100%', 'important');
      menu.style.setProperty('justify-content', 'center', 'important');
      menu.style.setProperty('text-align', 'center', 'important');
      menu.style.setProperty('margin-left', 'auto', 'important');
      menu.style.setProperty('margin-right', 'auto', 'important');

      Array.from(menu.children).forEach((item) => {
        item.style.setProperty('text-align', 'center', 'important');
      });
    });
  };

  const sync = (details) => {
    if (!details?.isConnected) {
      managed.delete(details);
      return;
    }

    const summary = details.querySelector(':scope > summary');

    if (mediaQuery.matches) {
      if (!mobileState.has(details)) mobileState.set(details, details.open);
      details.open = true;
      details.dataset.desktopOpen = 'true';
      summary?.setAttribute('tabindex', '-1');
      summary?.setAttribute('aria-disabled', 'true');
      return;
    }

    if (details.dataset.desktopOpen === 'true') {
      details.open = mobileState.get(details) ?? false;
      delete details.dataset.desktopOpen;
    }

    summary?.removeAttribute('tabindex');
    summary?.removeAttribute('aria-disabled');
  };

  const init = (root = document) => {
    const elements = [];

    if (root.matches?.(SELECTOR)) elements.push(root);
    root.querySelectorAll?.(SELECTOR).forEach((details) => elements.push(details));

    elements.forEach((details) => {
      managed.add(details);
      sync(details);
    });

    ensureAccordionStyles();
    applyBottomMenuAlignment(root);
  };

  const syncAll = () => managed.forEach(sync);
  const syncBottomMenu = () => applyBottomMenuAlignment(document);

  const destroy = (root) => {
    const elements = [];
    if (root.matches?.(SELECTOR)) elements.push(root);
    root.querySelectorAll?.(SELECTOR).forEach((details) => elements.push(details));
    elements.forEach((details) => managed.delete(details));
  };

  if (window[GLOBAL_KEY]) {
    ensureAccordionStyles();
    window[GLOBAL_KEY].init(document);
    applyBottomMenuAlignment(document);
    return;
  }

  const module = { init, destroy };
  window[GLOBAL_KEY] = module;

  mediaQuery.addEventListener?.('change', syncAll);
  mobileQuery.addEventListener?.('change', syncBottomMenu);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', () => {
    ensureAccordionStyles();
    applyBottomMenuAlignment(document);
  });
  document.addEventListener('shopify:section:reorder', () => {
    ensureAccordionStyles();
    applyBottomMenuAlignment(document);
  });
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));

  init(document);
})();
